import { Answers, IQuestionModel } from './consultation.dto';

/**
 * Database-backed questionnaire definitions.
 *
 * These replace the compile-time catalog in `@care-portals/data`
 * (`forms-data/questions/**` + `formTypes`), which is marked
 * "@deprecated ... get from db instead" but was never actually moved.
 *
 * DESIGN NOTE — fidelity over elegance.
 * The stored question shape is deliberately the SAME shape as the existing
 * `IQuestionModel`, not a cleaned-up redesign. 2,343 questions, ~50 Beluga
 * mapper classes that read raw answer keys as string literals, and every
 * historical consultation in the `consultations` collection all depend on the
 * current shape. Migrating the storage location and the question model at the
 * same time would make the codegen diff unverifiable.
 *
 * The only deliberate departures from the in-code shape are:
 *  1. Flow edges are stored as flat data (`nodes`) rather than a nested array
 *     of strings/objects — see {@link IQuestionnaireFlowNode}.
 *  2. Function-valued answers become {@link IQuestionnaireCalculatorRef}
 *     descriptors, because functions can't be persisted.
 *  3. Every definition is scoped by `organization` and `version`.
 */

/** Publication state. Only one `published` version per (organization, key). */
export type QuestionnaireStatus = 'draft' | 'published' | 'archived';

/**
 * Named, server-side calculators that replace the 14 function-valued `answer`
 * definitions in the code catalog (BMI and the clinical scoring instruments).
 *
 * A function can't be stored in Mongo, and an arbitrary expression language
 * would be a needless liability in a clinical path. There are only a handful of
 * distinct behaviours, so they are named and implemented in code instead.
 */
export type QuestionnaireCalculatorKey =
  | 'bmi'
  | 'phq9'
  | 'gad7'
  | 'epds'
  | 'dass21'
  | 'pcl5'
  | 'ssrs'
  | 'qadam';

/** Replaces a function value in a question's `answer` definition. */
export interface IQuestionnaireCalculatorRef {
  $calc: QuestionnaireCalculatorKey;
  /** Answer fields the calculator reads. Empty means "the whole answers object". */
  inputs?: string[];
}

/**
 * A comparison against a stored answer.
 *
 * NOTE: the code engine (`ConsultationUtils.checkCondition`) declares `$gt` in
 * its type but only ever implements `$ne` and strict equality — a `$gt`
 * condition silently evaluates to true. The DB engine implements all of these,
 * so codegen reports any flow that relied on the old behaviour rather than
 * quietly changing what a patient is asked.
 */
export type QuestionnaireConditionValue =
  | boolean
  | string
  | number
  | {
      $ne?: boolean | string | number;
      $gt?: number;
      $gte?: number;
      $lt?: number;
      $lte?: number;
      $in?: Array<string | number | boolean>;
    };

/**
 * Condition map. Keys are `${field}.${optionKey}` paths into the answers
 * object, matching the code catalog's convention. All entries must hold (AND).
 */
export type IQuestionnaireCondition = Record<string, QuestionnaireConditionValue>;

/** Where to go after a question is answered. */
export interface IQuestionnaireEdge {
  /** Target when `condition` holds (or unconditionally, when alone). */
  key?: string;
  /** Target when `condition` fails. Falls through to the next node when absent. */
  els?: string;
  condition?: IQuestionnaireCondition;
  /** First matching branch wins; `els` is the fallback. */
  switch?: Array<{ condition: IQuestionnaireCondition; key: string }>;
}

/**
 * One step in the flow.
 *
 * The code catalog stores flows as a nested array mixing bare strings, branch
 * objects, and sub-arrays used purely for grouping. Codegen flattens that to a
 * plain ordered list: a node with no `next` falls through to the following
 * node, which is exactly what a bare string meant. Flattening lets the engine
 * index nodes by key once instead of doing a `cloneDeep` breadth-first search
 * of the whole tree on every single question transition.
 */
export interface IQuestionnaireFlowNode {
  /** Question `field` this node refers to. */
  key: string;
  next?: IQuestionnaireEdge;
  /** Terminal node — the questionnaire ends here. */
  isLast?: boolean;
}

/**
 * A question definition. Structurally the stored form of {@link IQuestionModel};
 * `answer` may additionally contain {@link IQuestionnaireCalculatorRef} values
 * where the code catalog had functions.
 */
export type IQuestionnaireQuestion = IQuestionModel;

/** Provenance for a definition generated from the legacy code catalog. */
export interface IQuestionnaireSource {
  /** 'codegen' = generated from `@care-portals/data`; 'manual' = authored in the DB. */
  origin: 'codegen' | 'manual';
  /** Commit the catalog was generated from. */
  commit?: string;
  generatedAt?: Date;
  /** Anything codegen had to compromise on — carried so it stays visible. */
  warnings?: string[];
}

export class IQuestionnaire {
  _id?: string;
  organization: string;
  /** Stable questionnaire key, e.g. `wlus`. Equal to `consultation.type`. */
  key: string;
  /** Monotonic per (organization, key). */
  version: number;
  status: QuestionnaireStatus;

  /** Patient-facing title, from the old `FormType.name`. */
  name: string;
  description?: string;

  /** Every question reachable from this flow, keyed by `field`. */
  questions: IQuestionnaireQuestion[];
  /** Ordered flow. The first node is the entry point. */
  nodes: IQuestionnaireFlowNode[];

  /** Days a completed consultation stays valid. Was `FormType.validityLength`. */
  validityLength?: number;
  /** Earlier version numbers a viewer may need to render. Was `olderVersions`. */
  supersedes?: number[];

  source?: IQuestionnaireSource;
  createdAt?: Date;
  updatedAt?: Date;
}

/* -------------------------------------------------------------------------- */
/*  Runner contract (patient-api <-> patient-next)                            */
/* -------------------------------------------------------------------------- */

/**
 * The flow is evaluated server-side, so the client never needs the catalog.
 * This is what makes a patient-next runner viable: patient-web had to bundle
 * all 57k lines of `forms-data` to know which question came next, and that is
 * the same coupling that forced admin-web to hardcode its questionnaire list.
 */
export interface IConsultationRunnerProgress {
  /** 1-based position of the current question in the answered path. */
  index: number;
  /** Total questions on the current path — an estimate, since flows branch. */
  total: number;
  percent: number;
}

/** One questionnaire in the set the patient currently owes. */
export interface IConsultationRunnerForm {
  type: string;
  name: string;
  completed: boolean;
}

/** Everything the runner UI needs to draw one step. */
export interface IConsultationRunnerState {
  /** Draft consultation holding the answers so far. Absent before first save. */
  consultationId?: string;
  /** Questionnaire key currently being answered. */
  type: string;
  /** Definition version driving this run, stamped onto the consultation. */
  definitionVersion: number;
  name: string;

  /** Question to render. Null once every questionnaire is finished. */
  question: IQuestionnaireQuestion | null;
  /** Previously stored answer for `question`, for back-navigation. */
  answer?: unknown;
  /**
   * Files already uploaded for the current question, with presigned URLs so a
   * returning patient sees what they submitted instead of an empty picker.
   */
  storedFiles?: IRunnerStoredFile[];

  /**
   * Flow + answers so far. The client re-walks remaining nodes as the patient
   * picks an answer, so the step total follows the branch they are on rather
   * than staying frozen at the server's last estimate.
   */
  nodes: IQuestionnaireFlowNode[];
  answers: Answers;

  progress: IConsultationRunnerProgress;
  /** Ordered set of questionnaires, so the UI can show remaining names. */
  forms: IConsultationRunnerForm[];
  selectedIndex: number;

  canGoBack: boolean;
  /** Current question ends the questionnaire. */
  isLast: boolean;
  /** Current question is a rejection terminal (`reject: true`). */
  isReject: boolean;
  /** Every questionnaire in the set is done. */
  completed: boolean;

  /** Where to send the patient once everything is complete. */
  redirectUrl?: string;
  /**
   * Set when questionnaires migrated to Pathwai are still outstanding. Takes
   * priority over `redirectUrl` — Pathwai finishes the chain and honours the
   * redirect itself. Mirrors patient-web's `pathwaiHandoffUrl`.
   */
  pathwaiStartUrl?: string;
}

/**
 * One file already uploaded for the current question.
 *
 * The bytes never pass through the API: the client asks for a presigned URL
 * (`POST /consultations/runner/upload-url`), PUTs straight to S3, then reports
 * the resulting location here. Routing a photo through Lambda as base64 would
 * inflate it by a third and run into the request payload limit.
 *
 * Neither field is trusted. `resourceId` is checked against the current
 * question's upload targets, so a caller cannot write to arbitrary answer keys,
 * and `location` is checked to be a URL the API itself would have issued for
 * this customer.
 */
export interface IRunnerFileUpload {
  /** Answer key this file is stored under (`answers[resourceId] = location`). */
  resourceId: string;
  /** S3 URL returned alongside the presigned PUT. */
  location: string;
}

/** One file the client wants a presigned upload URL for. */
export interface IRunnerUploadTarget {
  /** Answer key the file will be stored under. Validated against the question. */
  resourceId: string;
  contentType: string;
  /** Bytes, so the API can reject an oversized file before presigning. */
  size: number;
}

/** Body of `POST /consultations/runner/upload-url`. */
export class RequestUploadUrlsDTO {
  /** Questionnaire the question belongs to. */
  type: string;
  /** Question the files attach to. Rejected if it isn't the current one. */
  field: string;
  files: IRunnerUploadTarget[];
}

/**
 * A questionnaire definition as served to a client.
 *
 * Response of `GET /questionnaires`. Trimmed to what is needed to run the flow
 * in the browser — the rest of `IQuestionnaire` is server bookkeeping.
 */
export interface IPublicQuestionnaire {
  key: string;
  name: string;
  /** 0 marks a definition still coming from the code catalog. */
  version: number;
  questions: IQuestionnaireQuestion[];
  nodes: IQuestionnaireFlowNode[];
}

/**
 * Body of `POST /consultations/:id/files`.
 *
 * The questionnaire is taken from the consultation, so only the question and
 * the files are named here.
 */
export class CreateConsultationFilesDTO {
  /** Question the files attach to. */
  field: string;
  files: IRunnerUploadTarget[];
}

/** A presigned destination for one file. */
export interface IRunnerUploadUrl {
  resourceId: string;
  /** Short-lived presigned PUT. Send the raw file as the body. */
  signedUrl: string;
  /** Where the file will live — pass back as `IRunnerFileUpload.location`. */
  location: string;
}

/** Body of `POST /consultations/runner/answer`. */
export class SubmitAnswerDTO {
  /** Draft consultation, when one already exists. */
  consultationId?: string;
  /** Questionnaire being answered. */
  type: string;
  /** Question the answer belongs to. Rejected if it isn't the current one. */
  field: string;
  /**
   * Answer payload, stored verbatim under `answers[field]`.
   *
   * Omitted for `file_upload` questions: patient-web never wrote a field-level
   * answer for those — the uploaded locations under `answers[resourceId]` ARE
   * the answer, which is why the flow engine needs a special case to tell
   * whether such a question has been answered.
   */
  answer?: unknown;
  /** Derived values from the old `FormType.saveState` hook. */
  states?: Record<string, unknown>;
  /** Files already uploaded for this question, to record before advancing. */
  files?: IRunnerFileUpload[];
}

/** A file already stored on the current question, for redisplay. */
export interface IRunnerStoredFile {
  resourceId: string;
  /** Short-lived presigned URL the browser can render directly. */
  url?: string;
  mimetype?: string;
}

/**
 * One stored file on a consultation, tagged with the question it answers.
 *
 * `GET /consultations/:id/files` returns every one of these for the
 * consultation, not just the current question's: the API no longer tracks which
 * question the patient is on, so the client asks once and holds them for the
 * run, grouping by `field` itself.
 */
export interface IConsultationStoredFile extends IRunnerStoredFile {
  /** Question this file is an answer to. */
  field: string;
}

/** Query for `GET /consultations/runner`. */
export class StartRunnerDTO {
  orderId?: string;
  /** Comma-separated questionnaire keys, matching patient-web's contract. */
  types?: string;
  redirectUrl?: string;
}

export class GetQuestionnairesDTO {
  key?: string;
  status?: QuestionnaireStatus;
  version?: number;
}
